import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { BusinessUser } from '@models/businesses';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EmailService } from '@services/email/email.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { initializeSyncStatus } from '../closer-sync-integration.util';
import { STATUSES } from '@models/statuses';
import { take } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';

export interface ICloserSyncRequestLawyerData {
	currentUser: BusinessUser;
	user: BusinessUser;
}

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-signature-request-modal',
	templateUrl: './closer-sync-signature-request-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncSignatureRequestModalComponent implements OnInit {
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;
	public user: BusinessUser;
	public currentUser: BusinessUser;
	public setupStarted: boolean;

	@SelectSnapshot(BusinessUsersState.isSyncIntegrationConnected)
	isSyncIntegrationConnected: boolean;

	constructor(
		private dialogRef: MatDialogRef<CloserSyncSignatureRequestModalComponent>,
		private snackBarService: SnackbarService,
		private emailService: EmailService,
		private businessUsersDispatch: BusinessUsersDispatch,
		@Inject(MAT_DIALOG_DATA) public data: ICloserSyncRequestLawyerData,
	) {}

	ngOnInit(): void {
		const { currentUser, user } = this.data || {};
		this.user = user;
		this.currentUser = currentUser;
		this.setupStarted =
			STATUSES.DISCONNECTED !== initializeSyncStatus(this.user);
	}

	public close = (): void => {
		this.dialogRef.close();
	};

	public sendEmail = (): void => {
		this.emailService
			.addToQueue({
				recipients: [
					{
						email: this.user.email,
						displayName: `${this.user.firstName} ${this.user.lastName}`,
					},
				],
				subject: `${this.isSyncIntegrationConnected ? 'Sync by Closer Setup Request' : 'Connect with Sync Invitation'}`,
				htmlMessage: `
				<div>
					<table width="100%" cellpadding="0" cellspacing="0" style="padding: 8px; border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
						<tr>
							<td align="center">
								<table cellpadding="0" cellspacing="0" style="border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
									<tr>
										<td align="center">
											<h2 style="font-family: 'Poppins', Arial, sans-serif; font-size: 1.5rem; font-weight: 600; color: #3C3B3E;">${this.isSyncIntegrationConnected ? 'Your setup has been requested' : 'Connect with Sync by Closer!'}</h2>
										</td>
									</tr>
									<tr>
										<td align="center" style="width: 384px;">
											<img src="https://i.imgur.com/2zHk3kq.png" width="384" style="width: 100%; height: auto; max-width: 384px;"/>
										</td>
									</tr>
									<tr>
										<td align="center">
											<p style="font-family: 'Poppins', Arial, sans-serif; color: #71717A;"><b>${this.currentUser.firstName} ${this.currentUser.lastName}</b>${this.isSyncIntegrationConnected ? ` has requested that you ${this.setupStarted ? 'complete' : 'start'} your setup on Sync by Closer's integration page.` : 'has invited you to connect with Sync for quicker, simpler mortgage processing!'}</p>
										</td>
									</tr>
									<tr>
										<td align="center">
											<a href="${window.location.origin}/app/profile/integrations/closer-sync?start-auth-flow=true" style="display: inline-block; text-decoration: none;">
												<div style="font-family: 'Poppins', Arial, sans-serif; padding: 8px 16px; border-radius: 0.375rem; background-color: #058078; color: white; text-align: center;">${this.isSyncIntegrationConnected ? `Click Here to ${this.setupStarted ? 'Complete' : 'Start'} Setup` : 'Get Started'}</div>
											</a>
										</td>
									</tr>
								</table>
							</td>
						</tr>
						<tr>
							<td align="center" style="width: 120; padding-top: 32px;">
								<img src="https://closer.lawlabs.ca/assets/images/integrations/sync-logo.png" width="120" style="width: 100%; height: auto; max-width: 120px;"/>
							</td>
						</tr>
					</table>
				</div>
				`,
				fromName: `${this.currentUser.firstName} ${this.currentUser.lastName}`,
				fromEmail: 'donotreply@lawlabs.ca',
			})
			.pipe(untilDestroyed(this), take(1))
			.subscribe(
				() => {
					this.businessUsersDispatch.SetSyncInviteReceived(true, this.user);
					this.snackBarService.openSnackBar({
						text: `An Invite To Connect With Sync Has Been Sent To ${this.user.firstName} ${this.user.lastName}`,
						type: 'success',
					});
					this.dialogRef.close();
				},
				() => {
					this.snackBarService.openSnackBar({
						text: 'Error sending email',
						type: 'error',
					});
				},
			);
	};
}
